import React from 'react'
import { Box, Button, CircularProgress, List, ListItemButton, ListItemText, Typography, useTheme, Tooltip, IconButton } from '@mui/material'
import AddCommentIcon from '@mui/icons-material/AddComment'
import MenuIcon from '@mui/icons-material/Menu'
import { useTranslation } from 'react-i18next'

import type { ClientSessionMetadata } from '@/core/models/medconnect-agent.model'

interface ChatHistoryContentProps {
  groupedSessions: Record<string, ClientSessionMetadata[]>
  isLoadingSessions: boolean
  sessionsError: any
  sessions: ClientSessionMetadata[] | undefined
  selectedSessionId: string | null | undefined
  onSessionSelect: (sessionId: string) => void
  onNewChat: () => void
  isCollapsed: boolean
  isMobile: boolean
  onCollapseToggle?: () => void
  detectedVariant: 'drawer' | 'sidebar'
}

export const ChatHistoryContent: React.FC<ChatHistoryContentProps> = ({
  groupedSessions,
  isLoadingSessions,
  sessionsError,
  sessions,
  selectedSessionId,
  onSessionSelect,
  onNewChat,
  isCollapsed,
  isMobile,
  onCollapseToggle,
  detectedVariant,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  return (
    <>
      {/* Header: New Chat button + Collapse button */}
      <Box
        sx={{
          p: 1,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Button
          fullWidth={!isCollapsed}
          variant="outlined"
          startIcon={<AddCommentIcon />}
          onClick={onNewChat}
          sx={{
            justifyContent: 'center',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            flex: isCollapsed ? 0 : 1,
            minWidth: isCollapsed ? 'auto' : undefined,
            px: isCollapsed ? 1 : 2,
          }}
        >
          {!isCollapsed && t('page.medconnectAgent.chatHistory.newChat')}
        </Button>
        {!isMobile && onCollapseToggle && (
          <Tooltip title={isCollapsed ? t('page.medconnectAgent.chatHistory.expand') : t('page.medconnectAgent.chatHistory.collapse')}>
            <IconButton
              size="small"
              onClick={onCollapseToggle}
              sx={{
                flexShrink: 0,
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.04)',
                },
              }}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <MenuIcon color="primary" fontSize="medium" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Scrollable content area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: '#ffffff',
          height: detectedVariant === 'drawer' ? `calc(100% - ${theme.spacing(7)})` : '100%',
        }}
      >
        {isLoadingSessions && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {sessionsError && (
          <Typography color="error" sx={{ p: 2, fontSize: '0.875rem' }}>
            {t('page.medconnectAgent.chatHistory.errorLoading')}
          </Typography>
        )}

        {!isLoadingSessions && !sessionsError && sessions && (
          <List dense disablePadding>
            {Object.entries(groupedSessions).map(
              ([groupName, groupSessionsList]) =>
                groupSessionsList.length > 0 && (
                  <React.Fragment key={groupName}>
                    {!isCollapsed && (
                      <Typography
                        sx={{
                          px: 2,
                          py: 1,
                          fontSize: '0.7rem',
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          fontWeight: 500,
                          letterSpacing: '0.5px',
                        }}
                      >
                        {groupName}
                      </Typography>
                    )}
                    {groupSessionsList.map((session) => {
                      const sessionTitle: string = session.title || t('page.medconnectAgent.chatHistory.newConversation') || ''
                      const isSelected = selectedSessionId === session.sessionId
                      return (
                        <Tooltip key={session.sessionId} title={isCollapsed ? sessionTitle : ''} placement="right" disableHoverListener={!isCollapsed}>
                          <ListItemButton
                            selected={isSelected}
                            onClick={() => onSessionSelect(session.sessionId)}
                            sx={{
                              mx: 1,
                              my: 0.5,
                              borderRadius: 2,
                              minHeight: 40,
                              '&.Mui-selected': {
                                bgcolor: 'rgba(0,0,0,0.08)',
                                '&:hover': {
                                  bgcolor: 'rgba(0,0,0,0.1)',
                                },
                              },
                              '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.04)',
                              },
                              justifyContent: isCollapsed ? 'center' : 'flex-start',
                              px: isCollapsed ? 1 : 2,
                            }}
                          >
                            {isCollapsed ? (
                              <Typography
                                sx={{
                                  fontWeight: isSelected ? 600 : 400,
                                  fontSize: '0.75rem',
                                  color: isSelected ? 'text.primary' : 'text.secondary',
                                }}
                              >
                                {sessionTitle.slice(0, 2).toUpperCase()}
                              </Typography>
                            ) : (
                              <ListItemText
                                primary={sessionTitle}
                                primaryTypographyProps={{
                                  fontSize: '0.875rem',
                                  fontWeight: isSelected ? 500 : 400,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              />
                            )}
                          </ListItemButton>
                        </Tooltip>
                      )
                    })}
                  </React.Fragment>
                )
            )}
            {sessions.length === 0 && (
              <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>
                {t('page.medconnectAgent.chatHistory.noHistory')}
              </Typography>
            )}
          </List>
        )}
      </Box>
    </>
  )
}
